import React, {useState} from 'react';
import AppButton from '@common/components/app-button';
import {ModalIds} from '@common/models/enums/modal-ids.enum';
import useModalWithData from '@common/hooks/use-modal-with-data';
import {ReactComponent as EyeSlashIcon} from '@assets/icons/eye-slash.svg';
import {ExclamationTriangleIcon} from "@heroicons/react/24/outline";
import {classNames} from '@utils/class-names';
import AppModal from "../../core/components/app-modal";
import {animated} from "@react-spring/web";
import {useModalAnimation} from "../../core/animations";

export interface DeactivateWidgetModalData {
    onConfirm?: () => void | Promise<void>;
}

const CONFIRMATION_WORD = 'UKRYJ';

const DeactivateWidgetModal: React.FC = () => {
    const {
        hideModal,
        data,
        isOpenModal
    } = useModalWithData<DeactivateWidgetModalData>(ModalIds.WIDGET_DEACTIVATE_CONFIRM);
    const [step, setStep] = useState<'first' | 'second'>('first');
    const [confirmationText, setConfirmationText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const {springProps, closeModal} = useModalAnimation(!!isOpenModal(), 'medium');

    const modalData = data as DeactivateWidgetModalData | null;
    if (!modalData) return null;

    const {onConfirm} = modalData;
    const isFirstStep = step === 'first';
    const isConfirmationValid = confirmationText === CONFIRMATION_WORD;

    const resetState = () => {
        setStep('first');
        setConfirmationText('');
    };

    const handleFirstConfirm = () => setStep('second');

    const handleSecondConfirm = async () => {
        if (!isConfirmationValid) return;
        try {
            setIsSubmitting(true);
            await onConfirm?.();
            hideModal();
            resetState();
        } catch (error) {
            console.error('Błąd podczas ukrywania aplikacji:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        closeModal(() => {
            resetState();
            hideModal();
        });
    };

    const handleBack = () => {
        setStep('first');
        setConfirmationText('');
    };

    return (
        <AppModal
            id={ModalIds.WIDGET_DEACTIVATE_CONFIRM}
            key={ModalIds.WIDGET_DEACTIVATE_CONFIRM}
            zIndex={50}
            forceFixed={true}>
            <animated.div
                style={springProps}
                className="h-full w-full flex flex-col bg-surface-level-1 rounded-2xl border border-outline-mild shadow-lvl-2 py-4 px-6 md:max-w-md md:min-w-[400px]"
            >
                <div className="flex items-center gap-3 mb-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-error/10 rounded-full flex items-center justify-center">
                        {isFirstStep
                            ? <EyeSlashIcon className="w-5 h-5 text-error"/>
                            : <ExclamationTriangleIcon className="w-5 h-5 text-error"/>}
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-on-surface">
                            {isFirstStep ? 'Ukryć aplikację?' : 'Potwierdź ukrycie'}
                        </h3>
                        <p className="text-sm text-on-surface/70">
                            {isFirstStep ? 'Zmieniasz widoczność na swojej stronie' : 'Wpisz słowo, aby potwierdzić'}
                        </p>
                    </div>
                </div>

                <div className="mb-6">
                    {isFirstStep ? (
                        <p className="text-on-surface/80 text-sm leading-relaxed">
                            Ukrycie aplikacji usunie ją z Twojej strony internetowej. Pacjenci, którzy ją odwiedzą,
                            nie zobaczą widgetu ani żadnych powiązanych treści - artykułów, specjalistów czy materiałów
                            dla pacjentów.
                        </p>
                    ) : (
                        <div className="space-y-4">
                            <p className="text-on-surface/80 text-sm leading-relaxed">
                                Twoje dane i ustawienia pozostaną nienaruszone. W każdej chwili możesz ponownie pokazać
                                aplikację, a wszystko wróci do poprzedniego stanu.
                            </p>
                            <div>
                                <p className="text-on-surface/80 text-sm mb-2">
                                    Aby potwierdzić, wpisz <span
                                    className="font-bold text-error">{CONFIRMATION_WORD}</span> w polu poniżej:
                                </p>
                                <input
                                    type="text"
                                    value={confirmationText}
                                    onChange={(e) => setConfirmationText(e.target.value.toUpperCase())}
                                    placeholder={`Wpisz: ${CONFIRMATION_WORD}`}
                                    className={classNames(
                                        "w-full p-2 border text-sm font-semibold rounded-lg transition-colors",
                                        "focus:outline-none focus:ring-2 focus:ring-primary/20",
                                        isConfirmationValid
                                            ? "border-success bg-success/5"
                                            : "border-outline-mild bg-surface-level-2"
                                    )}
                                    autoFocus
                                />
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex gap-3 flex-wrap md:flex-nowrap">
                    {isFirstStep ? (
                        <>
                            <AppButton
                                onClick={handleCancel}
                                className="flex-1 font-semibold border-2 border-on-surface hover:bg-on-surface/5"
                                disabled={isSubmitting}
                            >
                                Anuluj
                            </AppButton>
                            <AppButton
                                onClick={handleFirstConfirm}
                                className="flex-1 bg-error text-white font-semibold hover:bg-error/80"
                                disabled={isSubmitting}
                            >
                                Ukryj aplikację
                            </AppButton>
                        </>
                    ) : (
                        <>
                            <AppButton
                                onClick={handleBack}
                                className="flex-1 font-semibold border-2 border-on-surface hover:bg-on-surface/5"
                                disabled={isSubmitting}
                            >
                                Wróć
                            </AppButton>
                            <AppButton
                                onClick={handleSecondConfirm}
                                className="flex-1 bg-error text-white font-semibold hover:bg-error/80"
                                disabled={!isConfirmationValid || isSubmitting}
                                loading={isSubmitting}
                                loaderColor="#fff"
                            >
                                Tak, ukryj
                            </AppButton>
                        </>
                    )}
                </div>
            </animated.div>
        </AppModal>
    );
};

export default DeactivateWidgetModal;
